/* ==========================================================================
   Pop-up de amostras (section sampling-popup). Só age dentro de [data-smpop].
   Abre depois de N segundos; fechado (X, Esc, tocar fora) ou resgatado, só volta
   depois de N horas. "Resgatar agora" mostra a tela intermediária, em que cada
   etapa é real: estoque do kit → carrinho só com o kit → checkout.
   ========================================================================== */

(() => {
  if (window.__smpop) return;
  window.__smpop = true;

  const STORAGE_KEY = 'smpop-hidden-until';
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const STEP_MIN_MS = 900; // cada etapa fica pelo menos isso na tela, para dar para ler

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const atLeast = (promise, ms) => Promise.all([promise, wait(ms)]).then(([value]) => value);

  function isHidden() {
    try {
      return Date.now() < Number(localStorage.getItem(STORAGE_KEY) || 0);
    } catch (e) {
      return false;
    }
  }

  function hideFor(hours) {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + hours * 3600 * 1000));
    } catch (e) {
      /* sem localStorage: volta na próxima página */
    }
  }

  function track(event, data) {
    try {
      (window.dataLayer = window.dataLayer || []).push(Object.assign({ event }, data));
    } catch (e) {
      /* rastreio nunca impede o resgate */
    }
  }

  // Mesmas UTMs das landings de sampling (lp-sampling.js guarda na mesma chave)
  function visitUtms() {
    let utms = {};
    try {
      const params = new URLSearchParams(window.location.search);
      UTM_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) utms[key] = value.slice(0, 255);
      });
      if (Object.keys(utms).length) sessionStorage.setItem('lp-sampling-utms', JSON.stringify(utms));
      else utms = JSON.parse(sessionStorage.getItem('lp-sampling-utms') || '{}') || {};
    } catch (e) {
      /* sem sessionStorage: só as da URL */
    }
    return utms;
  }

  // Reserva sem carrinho: o link permanente leva só o kit ao checkout, com as UTMs no pedido
  function checkoutUrl(dialog) {
    const utms = visitUtms();
    const params = Object.keys(utms).map((key) => `attributes[${key}]=${encodeURIComponent(utms[key])}`);
    params.push('attributes[origem_resgate]=popup-amostras');
    return `/cart/${dialog.dataset.variant}:1?${params.join('&')}`;
  }

  function checkStock(dialog) {
    return fetch(`/products/${dialog.dataset.handle}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product) return true; // na dúvida, segue: o checkout confere o estoque de novo
        const variant = (product.variants || []).find((v) => String(v.id) === dialog.dataset.variant);
        return Boolean(variant ? variant.available : product.available);
      })
      .catch(() => true);
  }

  // Mesmo fluxo do "Resgatar" das landings (lp-sampling.js): carrinho só com o kit e as
  // UTMs da visita como atributos, que aparecem no pedido. Falhou, rejeita e vale o link.
  function prepareCart(dialog) {
    const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const attributes = { origem_resgate: 'popup-amostras' };
    const utms = visitUtms();
    UTM_KEYS.forEach((key) => (attributes[key] = utms[key] || ''));
    const ok = (response) => {
      if (!response.ok) throw new Error(`cart ${response.status}`);
      return response;
    };
    return fetch('/cart/clear.js', { method: 'POST', headers: json })
      .then(ok)
      .then(() =>
        fetch('/cart/add.js', {
          method: 'POST',
          headers: json,
          body: JSON.stringify({ items: [{ id: Number(dialog.dataset.variant), quantity: 1 }] }),
        })
      )
      .then(ok)
      .then(() => fetch('/cart/update.js', { method: 'POST', headers: json, body: JSON.stringify({ attributes }) }))
      .then(ok);
  }

  function trackRedeem(dialog) {
    const price = Number(dialog.dataset.price) || 0;
    track('sample_popup_redeem', { product: dialog.dataset.product, variant: dialog.dataset.variant });
    try {
      if (window.mixpanel && typeof window.mixpanel.track === 'function') {
        window.mixpanel.track('Added to Cart', { name: dialog.dataset.product });
      }
      if (window.Insider && typeof window.Insider.track === 'function') {
        window.Insider.track('events', [
          {
            event_name: 'ecom_add_to_cart',
            timestamp: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
            event_params: {
              pid: dialog.dataset.variant,
              c_product_name: dialog.dataset.product,
              p_price: price,
              c_event_source: 'popup-amostras',
            },
          },
        ]);
      }
    } catch (e) {
      /* rastreio nunca impede o resgate */
    }
  }

  function init(dialog) {
    if (dialog.dataset.smpopReady) return;
    dialog.dataset.smpopReady = 'true';

    const hours = Number(dialog.dataset.hideHours) || 24;
    const delay = (Number(dialog.dataset.delay) || 0) * 1000;
    const design = dialog.dataset.design === 'true';
    const redeem = dialog.querySelector('[data-smpop-redeem]');
    const offer = dialog.querySelector('[data-smpop-offer]');
    const loading = dialog.querySelector('[data-smpop-loading]');
    const steps = [...dialog.querySelectorAll('[data-smpop-step]')];
    const bar = dialog.querySelector('[data-smpop-bar]');
    let redeeming = false;
    let lastFocus = null;

    // As imagens são lazy, e dentro do pop-up fechado o navegador não as baixa nem
    // trocando para eager. Baixa cópias com o mesmo srcset/sizes (mesmo arquivo
    // escolhido, que fica no cache) durante a espera antes de abrir.
    let warmed = false;
    function warmImages() {
      if (warmed) return;
      warmed = true;
      dialog.querySelectorAll('img').forEach((img) => {
        const copy = new Image();
        if (img.sizes) copy.sizes = img.sizes;
        if (img.srcset) copy.srcset = img.srcset;
        copy.src = img.src;
        img.loading = 'eager';
      });
    }

    function open() {
      if (dialog.open) return;
      warmImages();
      lastFocus = document.activeElement;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('smpop-open');
      track('sample_popup_view', { product: dialog.dataset.product });
    }

    function close() {
      if (redeeming) return;
      if (!design) hideFor(hours);
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    }

    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('smpop-open');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    });

    // Esc: o <dialog> fecha sozinho; marca o fechamento (ou impede, durante o resgate)
    dialog.addEventListener('cancel', (event) => {
      if (redeeming) {
        event.preventDefault();
        return;
      }
      if (!design) hideFor(hours);
    });

    dialog.querySelectorAll('[data-smpop-close]').forEach((button) => button.addEventListener('click', close));

    // Tocar fora do conteúdo (no fundo do pop-up) não fecha: ele ocupa a tela toda
    // e o clique cairia em áreas vazias da própria oferta.

    function setStep(index, state) {
      steps.forEach((step, i) => {
        step.classList.toggle('is-done', i < index || (i === index && state === 'done'));
        step.classList.toggle('is-active', i === index && state === 'active');
        step.classList.toggle('is-error', i === index && state === 'error');
      });
      const done = index + (state === 'done' ? 1 : 0);
      bar.style.width = `${Math.round((done / steps.length) * 100)}%`;
    }

    redeem.href = checkoutUrl(dialog);
    redeem.addEventListener('click', async (event) => {
      event.preventDefault();
      if (redeeming) return;

      redeeming = true;
      hideFor(hours);
      trackRedeem(dialog);

      offer.hidden = true;
      loading.hidden = false;
      dialog.querySelector('.smpop__close').hidden = true;
      dialog.scrollTop = 0;
      setStep(0, 'active');

      const available = await atLeast(checkStock(dialog), STEP_MIN_MS);
      if (!available) {
        setStep(0, 'error');
        redeeming = false;
        dialog.querySelector('.smpop__close').hidden = false;
        dialog.querySelectorAll('[data-smpop-sold-out]').forEach((el) => (el.hidden = false));
        track('sample_popup_sold_out', { product: dialog.dataset.product });
        return;
      }

      setStep(1, 'active');
      const cartReady = await atLeast(
        prepareCart(dialog).then(
          () => true,
          () => false
        ),
        STEP_MIN_MS
      );

      setStep(2, 'active');
      await wait(STEP_MIN_MS);
      setStep(2, 'done');
      window.location.href = cartReady ? '/checkout' : redeem.href;
    });

    // Voltar do checkout pelo navegador (bfcache) restaura a página na tela de
    // carregamento: volta à oferta e fecha (já resgatou, só aparece de novo em N horas)
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted || !redeeming) return;
      redeeming = false;
      offer.hidden = false;
      loading.hidden = true;
      dialog.querySelector('.smpop__close').hidden = false;
      setStep(0, 'idle');
      bar.style.width = '0%';
      if (dialog.open) dialog.close();
    });

    // Editor de temas: abre ao selecionar a section, sem contar tempo nem guardar estado
    if (design) {
      document.addEventListener('shopify:section:select', (event) => {
        if (dialog.closest(`#shopify-section-${event.detail.sectionId}`)) open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (dialog.closest(`#shopify-section-${event.detail.sectionId}`)) close();
      });
      return;
    }

    if (isHidden()) return;

    // Depois do load da página, para não disputar banda com as imagens dela
    const warmWhenIdle = () => (window.requestIdleCallback || setTimeout)(warmImages, { timeout: 2000 });
    if (document.readyState === 'complete') warmWhenIdle();
    else window.addEventListener('load', warmWhenIdle, { once: true });

    // Espera o tempo configurado; se outro modal estiver aberto, tenta de novo depois
    const tryOpen = () => {
      if (isHidden()) return;
      const busy = document.querySelector('dialog[open]:not([data-smpop]), cart-drawer.active, .drawer.active');
      if (busy) {
        setTimeout(tryOpen, 3000);
        return;
      }
      open();
    };
    setTimeout(tryOpen, delay);
  }

  function initAll() {
    document.querySelectorAll('[data-smpop]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', initAll);
})();
